/**
 * 招待コードの「渡し方」＝事前充填リンクと QR（T-D30・06 §2.2）。
 *
 * ★ LP が約束しているのは「招待が届く→友だち追加→いつも通り注文」で、**8桁を手で打つ**
 *   摩擦は見せていない。設計側で摩擦を消すのがここ＝コードを充填した LIFF リンクと、
 *   その QR を発行と同時に出す。紙・口頭運用（OR-01）のために QR は必須。
 *
 * ★ **クエリ名は `invite`**（`code` は使わない）：外部ブラウザで LIFF を開くと LINE Login の
 *   認可コードが**同じ `code` という名前で**同じ URL に乗る。LIFF SDK のクエリ解析は重複キーを
 *   配列にするので、トークン交換の本文が `code=<招待>&code=<認可>` になって**ログインごと失敗**
 *   する（＝招待リンクを配ると誰も入れない）。06 §2.2 の 2026-07-26 訂正。
 */

import { Card } from "@kenzai/ui";
import { config } from "@kenzai/config";
import { useEffect, useState } from "react";


/** 事前充填リンク。LIFF ID が未設定なら null（＝リンクを作れないことを画面で言う）。 */
export function inviteLink(code: string): string | null {
  return config.liffId ? `https://liff.line.me/${config.liffId}/invite?invite=${code}` : null;
}

function useQrDataUrl(text: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!text) {
      setUrl(null);
      return;
    }
    let alive = true;
    // ★**動的 import**＝QR を使うのはこの画面だけ。静的に持つと console の初回読み込みに
    //   毎回 20KB 余分に乗る（S19 でモバイル対応する以上、初期バンドルは軽くしておく）。
    // 誤り訂正 M＝印刷して現場に貼る前提（多少汚れても読める）。余白は既定より小さく。
    void import("qrcode")
      .then((m) => m.default.toDataURL(text, { errorCorrectionLevel: "M", margin: 2, width: 220 }))
      .then((d) => {
        if (alive) setUrl(d);
      })
      .catch(() => {
        if (alive) setUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [text]);
  return url;
}

export function InviteShare({ code, compact = false }: { code: string; compact?: boolean }) {
  const link = inviteLink(code);
  const qr = useQrDataUrl(link);
  const [copied, setCopied] = useState(false);

  if (!link) {
    // ★ErrorNote は使わない＝あれは message プロパティを見るので、**素の文字列を渡すと
    //   「エラーが発生しました。」という別の文言に化ける**（実測）。ここは異常ではなく
    //   「この環境では作れない」という案内なので、落ち着いた注記で出す。
    return (
      <p
        role="note"
        data-testid="invite-share-unavailable"
        style={{
          fontSize: "var(--kz-font-size-sm)",
          color: "var(--kz-color-ink-muted)",
          background: "var(--kz-color-surface-sunken)",
          borderRadius: "var(--kz-radius-sm)",
          padding: "var(--kz-space-3)",
          margin: 0,
        }}
      >
        この画面では招待リンク・QR を作れません（LIFF ID が設定されていません）。
        コードを口頭・紙でお伝えください。
      </p>
    );
  }

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-3)", justifyItems: "center" }} data-testid="invite-share">
      {qr ? (
        // 印刷して渡せるように **画像そのもの**を出す（右クリックで保存できる）
        <img
          src={qr}
          alt={`招待コード ${code} の QR コード`}
          width={compact ? 160 : 220}
          height={compact ? 160 : 220}
          data-testid="invite-qr"
          style={{ borderRadius: "var(--kz-radius-sm)", background: "#fff", padding: 8 }}
        />
      ) : (
        <div style={{ width: compact ? 160 : 220, height: compact ? 160 : 220 }} aria-hidden />
      )}
      <div
        data-testid="invite-link"
        style={{
          fontSize: "var(--kz-font-size-sm)",
          color: "var(--kz-color-ink-muted)",
          wordBreak: "break-all",
          textAlign: "center",
        }}
      >
        {link}
      </div>
      <button
        type="button"
        className="kz-btn"
        data-testid="copy-invite-link"
        onClick={() => {
          // クリップボードは権限・非セキュアコンテキストで落ちる＝**落ちても画面は壊さない**
          void navigator.clipboard?.writeText(link).then(
            () => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            },
            () => setCopied(false),
          );
        }}
      >
        {copied ? "コピーしました" : "リンクをコピー"}
      </button>
      <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)", textAlign: "center" }}>
        QR を読む、またはリンクを開くと LINE が立ち上がり、コードが入った状態で
        <br />
        「接続する」を押すだけで使い始められます。
      </div>
    </div>
  );
}

/** 一覧の行から呼ぶ小さな重ね（行は狭いので、その場では出さない）。 */
export function InviteShareDialog({ code, onClose }: { code: string; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`招待コード ${code} の渡し方`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
        padding: "var(--kz-space-4)",
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, width: "100%" }}>
        <Card>
          <div style={{ display: "grid", gap: "var(--kz-space-3)", justifyItems: "center" }}>
            <div
              className="kz-num"
              style={{ fontSize: 28, fontWeight: 700, letterSpacing: "0.2em", color: "var(--kz-color-primary)" }}
            >
              {code}
            </div>
            <InviteShare code={code} compact />
            <button type="button" className="kz-btn" onClick={onClose} data-testid="close-invite-share">
              閉じる
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
